import { ParticipantsActions,  ParticipantsListFields } from "../types";
import { useTranslation } from "react-i18next";
import { RouteNavigationService } from "../../../../utils/navegation/RouteNavegationService";
import ModalGlobal from '../../../../components/AppModal/ModalGlobal';
import { useNavigate } from "react-router-dom";
import { useModal } from "../../../../hooks/useModal";
import { useParticipantCount } from "../hooks/useParticipantCount";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import { Pagination } from "../../../../types/Pagination";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { downloadParticipantsTemplate } from "../../surveys/api/surveyAPI";

interface ParticipantsTableProps {
    participantsData: Pagination<ParticipantsListFields>;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    participantsActions: ParticipantsActions; 
    onExportCsv: () => void; 
}

export default function ParticipantsTable({ participantsData, participantsActions, pageSize, currentPage, totalPages, onExportCsv }: ParticipantsTableProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const navigationService = new RouteNavigationService(navigate);
    
    const PARTICIPANT_MODAL_HEADERS = {
        titleDelete: t("participant:modal.participantModalHeaderDelete1"),
        subtitleDelete: t("participant:modal.participantModalHeaderDelete2"),
        titleExport: t("common:confirmation"),
        subtitleExport: t("participant:modal.participantModalHeaderExport2") || t("participant:exportAction.ConfirmationMessage"),
    };

    const csvColumnKeys = [
        "externalApp",
        "externalAppPatientId",
        "nhc",
        "name",
        "surname",
        "surname2",
        "birthDate",
        "birthCountry",
        "zipCode",
        "city",
        "province",
        "region",
        "country",
        "phoneNumber",
        "email",
        "externalAppCampaignId",
        "externalAppCampaignDescription",
        "externalAppSurveyId",
        "externalAppSurveyDescription",
        "eventDate",
        "medicalActTypeCode",
        "descriptionTypeMedicalAct",
        "externalCenterCode",
        "centerName",
        "externalMedicalServiceCode",
        "medicalServiceDescription",
        "unitCode",
        "unitDescription",
        "floor",
        "block"
    ];

    const csvColumns = csvColumnKeys.map(key =>
        t(`surveyAdmin:form.participants.csvHeaders.${key}`)
    );


    const columns: ColumnConfig<ParticipantsListFields>[] = [
        { 
            header: t("participant:participantListHeader1"), 
            key: "id",
            render: (item) => <>{item.id}</>
        },
        { 
            header: t("participant:participantListHeader2"), 
            key: "name",
            render: (item) => <>{`${item.name} ${item.surname}  ${item.surname2}`}</>
        },
        { 
            header: t("participant:participantListHeader3"), 
            key: "birthDate"
        },
        { header: t("participant:participantListHeader4"), key: "zipCode" },
        { header: t("participant:participantListHeader5"), key: "phone" },
        { header: t("participant:participantListHeader6"), key: "email" },
        { header: t("participant:participantListHeader7"), key: "campaingNames" },
    ];

    const { showModal, modalData, errorModal, endDate, openModal: openDeleteModal, closeModal, setLoading, isLoading, currentItem } = useModal<ParticipantsListFields>();

    const { fetchCount } = useParticipantCount();

    const handleDelete = async (participant: ParticipantsListFields) => {
        const relatedCount = await fetchCount(participant);
        const subtitle2 = relatedCount !== null
            ? t("participant:modal.participantDeleteMissageCount", { count: relatedCount })
            : "Unable to load related records count";

        openDeleteModal(
            participant,
            PARTICIPANT_MODAL_HEADERS.titleDelete,
            PARTICIPANT_MODAL_HEADERS.subtitleDelete + " " + participant.name + " " + participant.surname + " ?",
            "deleteWithoutInput",
            subtitle2
        );
    };

    const handleExportCsv = () => {
        onExportCsv(); 
    }

    const handleSaveModal = async () => {
        setLoading(true); // Iniciar loading
 
        try {
            if (modalData.type === 'deleteWithoutInput') {

                const participantDeleteDto = {
                    id: currentItem!.id,
                    email: currentItem!.email , 
                    phone: currentItem!.phone,
                };

                const countParticipantsDelete = await participantsActions.onDelete(participantDeleteDto);
                const stringCountParticipantsDelete = countParticipantsDelete.numberParicipantsDelete !== null
                    ? t("participant:modal.deleteActionParticipantDeleteSuccess", { count2: countParticipantsDelete.numberParicipantsDelete })
                    : "Unable to load related records count";

                navigationService.navigateWithMessage('/admin/participants', stringCountParticipantsDelete);
            }
            closeModal();
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false); // Finalizar loading
        }
    }

    const handleViewParticipant = (id: number) => {
         participantsActions.onView(id);

    };

    return (
        <div>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={modalData.title}
                contentType={modalData.type}
                subtitle={modalData.subtitle}
                subtitle2={modalData.subtitle2}
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                error={errorModal?.endDate || errorModal?.errorModal}
                endDate={endDate}
                onSave={() => handleSaveModal()}
                isLoading={isLoading}
            />
            <AppTable<ParticipantsListFields>
                data={participantsData.items || []}
                columns={columns}
                emptyMessage={t("participant:nonParticipants")}
                t={t}
                hasActions={true}
                onDelete={handleDelete}
                onView={handleViewParticipant}
                hasPagination={true}
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                paginationType="participant"
                onExportCsv={handleExportCsv}
                sectionPermission={SectionPermissionTypeEnum.PARTICIPANTS}
                onDownloadTemplate={() => downloadParticipantsTemplate(csvColumns, "import_participants_template.xlsx")}
                downloadTemplateText={t("surveyAdmin:form.participants.downloadTemplate")}
             
            />
        </div>
    )
}
